import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureException } from '../lib/sentry';
import { TDS } from '../constants/tds';
import { TdsButton } from './tds/TdsButton';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureException(error, {
      contexts: {
        react: { componentStack: info.componentStack ?? undefined },
      },
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? <DefaultFallback />;
    }
    return this.props.children;
  }
}

function DefaultFallback() {
  return (
    <div
      role="alert"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: TDS.spacing.xxl,
        backgroundColor: TDS.color.bgPrimary,
        textAlign: 'center',
      }}
    >
      <h2
        style={{
          ...TDS.typography.h2,
          color: TDS.color.textPrimary,
          marginBottom: TDS.spacing.sm,
        }}
      >
        일시적인 오류가 발생했어요
      </h2>
      <p
        style={{
          ...TDS.typography.body2,
          color: TDS.color.textSecondary,
          marginBottom: TDS.spacing.xxl,
        }}
      >
        앱을 다시 실행하시면 대부분 해결돼요.
      </p>
      <TdsButton variant="primary" size="md" onClick={() => window.location.reload()}>
        다시 시도
      </TdsButton>
    </div>
  );
}
